import { useEffect, useState } from "react";
import { getUrl } from "aws-amplify/storage";

import { useUserContext } from "../../contexts/UserContext";
import { useUserModule, UserModuleProvider } from "../../contexts/UserModuleContext";
import { PersonalInfoTab } from "./tabs/PersonalInfoTab";
import { EmploymentTab } from "./tabs/EmploymentTab";
import { SkillsetsTab } from "./tabs/SkillsetsTab";
import { MediaFilesTab } from "./tabs/MediaFilesTab";
import MyTrainingPage from "../safety-training/MyTrainingPage";

import "../../css/MyProfilePage.css";

type MyProfileTab = "personal" | "employment" | "my-training" | "my-skillsets" | "my-files";

const TABS: { id: MyProfileTab; label: string }[] = [
    { id: "personal", label: "Personal Info" },
    { id: "employment", label: "Employment" },
    { id: "my-training", label: "My Training" },
    { id: "my-skillsets", label: "My Skillsets" },
    { id: "my-files", label: "My Files" },
];

/**
 * Inner component that uses UserModuleContext.
 * Renders the employee self-view profile page (read-only).
 *
 * Validates: Requirements 12.1, 12.2, 12.3, 12.4, 13.1, 13.2, 14.1, 14.2, 15.1, 15.2
 */
const MyProfilePageInner = () => {
    const { companyUser, companyOwner } = useUserContext();
    const { fetchSkillAssignments, fetchUserFiles } = useUserModule();

    const [activeTab, setActiveTab] = useState<MyProfileTab>("personal");
    const [photoUrl, setPhotoUrl] = useState("");

    // Use the current logged-in user
    const currentUser = companyUser || companyOwner;

    // Fetch user-specific data on mount
    useEffect(() => {
        if (currentUser?.id) {
            fetchSkillAssignments(currentUser.id);
            fetchUserFiles(currentUser.id);
        }
    }, [currentUser?.id]);

    // Load user photo
    useEffect(() => {
        if (currentUser && (currentUser as any).userPhoto) {
            loadPhoto((currentUser as any).userPhoto);
        } else {
            setPhotoUrl("");
        }
    }, [currentUser]);

    const loadPhoto = async (path: string) => {
        try {
            const result = await getUrl({ path });
            setPhotoUrl(result.url.href);
        } catch (err) {
            console.error("Error loading user photo:", err);
        }
    };

    const getInitials = (name?: string | null): string => {
        if (!name) return "?";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (parts[0][0] || "?").toUpperCase();
    };

    const getRoleBadgeClass = (role?: string | null): string => {
        if (!role) return "default";
        const lower = role.toLowerCase();
        if (lower.includes("admin")) return "admin";
        if (lower.includes("operator")) return "operator";
        if (lower.includes("inspector")) return "inspector";
        if (lower.includes("owner")) return "owner";
        return "default";
    };

    const formatRoleName = (role?: string | null): string => {
        if (!role) return "—";
        return role
            .split("-")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    if (!currentUser) {
        return (
            <div className="page-container myp-page">
                <div className="myp-loading">Loading your profile...</div>
            </div>
        );
    }

    return (
        <div className="page-container myp-page">
            {/* Header Section */}
            <div className="myp-header">
                <div className="myp-header-content">
                    <div className="myp-avatar">
                        {photoUrl ? (
                            <img src={photoUrl} alt={currentUser.name || "User"} />
                        ) : (
                            <span className="myp-avatar-initials">
                                {getInitials(currentUser.name)}
                            </span>
                        )}
                    </div>
                    <div className="myp-header-info">
                        <h1 className="myp-user-name">{currentUser.name || "My Profile"}</h1>
                        <div className="myp-header-meta">
                            <span className={`myp-role-badge ${getRoleBadgeClass((currentUser as any).role)}`}>
                                {formatRoleName((currentUser as any).role)}
                            </span>
                            {(currentUser as any).currentJobClassification && (
                                <span className="myp-job-classification">
                                    {(currentUser as any).currentJobClassification}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* No edit button — read-only page */}
                </div>

                {/* Tab Bar */}
                <div className="myp-tabs">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            className={`myp-tab ${activeTab === tab.id ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div className="myp-tab-content">
                {activeTab === "personal" && (
                    <PersonalInfoTab
                        user={currentUser}
                        sensitiveInfo={null}
                        isEditing={false}
                        readOnly={true}
                        photoPreviewUrl={photoUrl}
                    />
                )}
                {activeTab === "employment" && (
                    <EmploymentTab
                        user={currentUser}
                        isEditing={false}
                        readOnly={true}
                    />
                )}
                {activeTab === "my-training" && (
                    <MyTrainingPage />
                )}
                {activeTab === "my-skillsets" && (
                    <SkillsetsTab
                        user={currentUser}
                        readOnly={true}
                    />
                )}
                {activeTab === "my-files" && (
                    <MediaFilesTab
                        user={currentUser}
                        readOnly={true}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * MyProfilePage wraps the inner component with UserModuleProvider.
 * Accessible to ALL authenticated users via the "My Profile" nav item.
 */
export const MyProfilePage = () => {
    return (
        <UserModuleProvider>
            <MyProfilePageInner />
        </UserModuleProvider>
    );
};
